/**
 * App Store (Pinia)
 *
 * @version 2.0.0
 * @description Global application state — aligned with Vuex app module
 * @migrated-from store/app/ (5 files: state, getters, actions, mutations, index)
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { isNil, standardizeErrorMessage } from '@/utils/utils'
import { setI18nLanguage, type SupportedLocale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

// =============================================================================
// Types
// =============================================================================

export interface UserPreferences {
  [key: string]: any
}

export type SnackbarType = 'success' | 'info' | 'error'

// =============================================================================
// Store
// =============================================================================

export const useAppStore = defineStore('app', () => {
  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  const baseUrl = import.meta.env.VITE_API_URL as string

  // ---------------------------------------------------------------------------
  // State — App metadata
  // ---------------------------------------------------------------------------

  const appTitle = ref(import.meta.env.VITE_APP_TITLE || 'Gandolfi CMS')
  const appShortTitle = ref(import.meta.env.VITE_APP_SHORT_TITLE || 'CTF CMS')
  const appVersion = ref('2.185.0')

  // ---------------------------------------------------------------------------
  // State — Network
  // ---------------------------------------------------------------------------

  const networkOnLine = ref(navigator.onLine)

  // ---------------------------------------------------------------------------
  // State — PWA / Service Worker
  // ---------------------------------------------------------------------------

  /** Truthy when a new SW version is available for activation */
  const SWRegistrationForNewContent = ref<ServiceWorkerRegistration | boolean | null>(null)
  const showSplashScreen = ref(false)
  const splashShown = ref(false)
  const refreshingApp = ref(false)

  // ---------------------------------------------------------------------------
  // State — UI
  // ---------------------------------------------------------------------------

  const loading = ref(false)
  const menuClosed = ref(false)
  const isPageFullScreen = ref(false)

  /**
   * Whether the global Preferences dialog is open.
   *
   * Lives in the store (not in a single component) because the dialog is
   * mounted ONCE at the App.vue root, but is opened from multiple places
   * that are mutually exclusive at runtime: AppToolbar (desktop user menu)
   * and AppNavigation (mobile drawer). Centralizing here keeps both call
   * sites trivial — they just toggle the boolean — and removes the need to
   * duplicate the dialog inside each shell component.
   */
  const preferencesDialogOpen = ref(false)

  // ---------------------------------------------------------------------------
  // State — Language
  // ---------------------------------------------------------------------------

  const currentLanguage = ref<string | null>(null)

  // ---------------------------------------------------------------------------
  // State — Analytics & Device
  // ---------------------------------------------------------------------------

  const analyticsEnabled = ref(import.meta.env.VITE_ENABLE_GA === '1')
  const deviceID = ref<string | null>(null)

  // ---------------------------------------------------------------------------
  // State — Snackbar
  // ---------------------------------------------------------------------------

  const snackbarMessage = ref('')
  const showSnackbarSuccessMessage = ref(false)
  const showSnackbarInfoMessage = ref(false)
  const showSnackbarErrorMessage = ref(false)

  // ---------------------------------------------------------------------------
  // State — User Preferences
  // ---------------------------------------------------------------------------

  const userPreferences = ref<UserPreferences>({})

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  const isOnline = computed(() => networkOnLine.value)
  const isLoading = computed(() => loading.value)

  /** Whether a new SW version is available for activation */
  const newContentAvailable = computed(() => !isNil(SWRegistrationForNewContent.value))

  // ---------------------------------------------------------------------------
  // Getter — User Preference by key
  // ---------------------------------------------------------------------------

  function getUserPreference(key: string): any {
    return userPreferences.value?.[key] ?? null
  }

  // ---------------------------------------------------------------------------
  // Actions — PWA / Service Worker
  // ---------------------------------------------------------------------------

  /**
   * Attiva il service worker in attesa e ricarica sulla versione nuova.
   *
   * FE v2.172.1 — BUGFIX overlay "Caricamento nuovi contenuti..." bloccato per
   * sempre. Prima: cancellava tutte le cache e poi mandava SKIP_WAITING al SW
   * in attesa, affidandosi SOLO al suo cambio di controllo per ricaricare. Se
   * il SW in attesa non c'era piu' (gia' attivato da un'altra finestra/scheda,
   * sostituito da un deploy successivo) il messaggio non arrivava a nessuno:
   * niente reload, overlay persistente, nessuna via d'uscita.
   *
   * Ora il reload e' GARANTITO, in tre modi (vince il primo):
   *   1. `controllerchange`: il SW nuovo ha preso il controllo (caso normale)
   *   2. nessun SW in attesa: reload immediato (la rete serve la versione nuova)
   *   3. rete di sicurezza: reload comunque dopo 5 secondi
   *
   * Le cache NON vengono piu' svuotate prima dell'attivazione: si cancellava
   * anche il precache appena scaricato dal SW nuovo, senza alcun beneficio
   * (le versioni vecchie le ripulisce Workbox, `cleanupOutdatedCaches`). Le
   * cache incoerenti restano compito del watchdog di index.html (v2.171.8).
   */
  async function serviceWorkerSkipWaiting(): Promise<void> {
    if (isNil(SWRegistrationForNewContent.value)) return
    if (refreshingApp.value) return // doppio click: gia' in corso

    refreshingApp.value = true

    let reloaded = false
    const reloadOnce = (): void => {
      if (reloaded) return
      reloaded = true
      window.location.reload()
    }

    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
    sw?.addEventListener('controllerchange', reloadOnce, { once: true })
    window.setTimeout(reloadOnce, 5000)

    let waiting: ServiceWorker | null = null
    try {
      waiting = (await sw?.getRegistration())?.waiting ?? null
    } catch (e) {
      console.warn('[PWA] getRegistration failed:', e)
    }

    if (!waiting) {
      console.warn('[PWA] nessun service worker in attesa: reload diretto')
      reloadOnce()
      return
    }

    // vite-plugin-pwa updateServiceWorker (esposto da main.ts); in mancanza,
    // il messaggio che Workbox si aspetta, mandato direttamente.
    const updateSW = (window as any).__pwaUpdateSW
    try {
      if (typeof updateSW === 'function') await updateSW(true)
      else waiting.postMessage({ type: 'SKIP_WAITING' })
    } catch (e) {
      console.warn('[PWA] updateServiceWorker failed, forcing reload:', e)
      reloadOnce()
    }
  }

  /**
   * Dismiss the SW update notification without activating.
   */
  function dismissServiceWorkerUpdate(): void {
    SWRegistrationForNewContent.value = null
  }

  /**
   * Set the SW registration (called from register-service-worker).
   */
  function setSWRegistrationForNewContent(reg: ServiceWorkerRegistration | boolean | null): void {
    SWRegistrationForNewContent.value = reg
  }

  // ---------------------------------------------------------------------------
  // Actions — Device ID
  // ---------------------------------------------------------------------------

  function initDeviceID(): void {
    let id = localStorage.getItem('deviceID')
    if (!id) {
      id = generateUUID()
      localStorage.setItem('deviceID', id)
    }
    deviceID.value = id
  }

  // ---------------------------------------------------------------------------
  // Actions — Language
  // ---------------------------------------------------------------------------

  function initCurrentLanguage(): void {
    const savedLang = localStorage.getItem('userPreferredLanguage')
    let lang = savedLang || navigator.language || 'it'

    if (lang.includes('-')) {
      lang = lang.split('-')[0]
    }

    currentLanguage.value = lang
    setI18nLanguage(lang as SupportedLocale)
  }

  function changeLanguage(lang: string): void {
    currentLanguage.value = lang
    localStorage.setItem('userPreferredLanguage', lang)
    setI18nLanguage(lang as SupportedLocale)
  }

  // ---------------------------------------------------------------------------
  // Actions — UI State
  // ---------------------------------------------------------------------------

  function toggleMenuClosed(): void {
    menuClosed.value = !menuClosed.value
  }

  function setMenuClosed(value: boolean): void {
    menuClosed.value = value
  }

  function setLoadingState(value: boolean): void {
    loading.value = value
  }

  function setPageFullScreen(value: boolean): void {
    isPageFullScreen.value = value
  }

  function setShowSplashScreen(value: boolean): void {
    showSplashScreen.value = value
  }

  function setSplashShown(value: boolean): void {
    splashShown.value = value
  }

  function setRefreshingApp(value: boolean): void {
    refreshingApp.value = value
  }

  function setNetworkOnline(value: boolean): void {
    networkOnLine.value = value
  }

  /**
   * Open the global Preferences dialog (mounted in App.vue).
   * Called from AppToolbar (desktop) and AppNavigation (mobile).
   */
  function openPreferencesDialog(): void {
    preferencesDialogOpen.value = true
  }

  // ---------------------------------------------------------------------------
  // Actions — Snackbar (unified + legacy compat)
  // ---------------------------------------------------------------------------

  /**
   * Unified snackbar method (new API).
   *
   * @example
   * showSnackbar('success', 'Operazione completata!')
   * showSnackbar('error', 'Errore di rete')
   */
  function showSnackbar(type: SnackbarType, message: string): void {
    snackbarMessage.value = message
    showSnackbarSuccessMessage.value = type === 'success'
    showSnackbarInfoMessage.value = type === 'info'
    showSnackbarErrorMessage.value = type === 'error'
  }

  /** @deprecated Use `showSnackbar('success', msg)` */
  function showSuccess(message: string): void {
    showSnackbar('success', message)
  }

  /** @deprecated Use `showSnackbar('info', msg)` */
  function showInfo(message: string): void {
    showSnackbar('info', message)
  }

  /** @deprecated Use `showSnackbar('error', msg)` */
  function showError(message: string): void {
    showSnackbar('error', message)
  }

  function resetSnackbar(): void {
    showSnackbarSuccessMessage.value = false
    showSnackbarInfoMessage.value = false
    showSnackbarErrorMessage.value = false
  }

  // ---------------------------------------------------------------------------
  // Actions — User Preferences
  // ---------------------------------------------------------------------------

  /**
   * Load user preferences from localStorage.
   * Uses useAuthStore to resolve current user if no userId provided.
   */
  function loadUserPreferences(userId?: number): void {
    const uid = userId ?? useAuthStore().user?.id
    if (uid == null) {
      console.warn('[useAppStore] loadUserPreferences: no user ID available')
      return
    }

    const stored = localStorage.getItem('usersPreferences')
    if (stored) {
      try {
        const all = JSON.parse(stored)
        userPreferences.value = all[uid] ?? {}
      } catch (e) {
        console.error('[useAppStore] Failed to parse user preferences:', e)
      }
    }
  }

  /**
   * Set a single user preference (persisted to localStorage).
   * Uses useAuthStore to resolve current user if no userId provided.
   */
  function setUserPreference(key: string, value: any, userId?: number): void {
    const uid = userId ?? useAuthStore().user?.id
    if (uid == null) {
      console.warn('[useAppStore] setUserPreference: no user ID available')
      return
    }

    const stored = localStorage.getItem('usersPreferences')
    let all: Record<number, UserPreferences> = {}
    if (stored) {
      try {
        all = JSON.parse(stored)
      } catch (e) {
        console.error('[useAppStore] Failed to parse user preferences:', e)
      }
    }

    if (!all[uid]) all[uid] = {}
    all[uid][key] = value
    userPreferences.value = all[uid]
    localStorage.setItem('usersPreferences', JSON.stringify(all))
  }

  // ---------------------------------------------------------------------------
  // Actions — API
  // ---------------------------------------------------------------------------

  /**
   * Send company profile email via backend.
   */
  async function sendCompanyProfile(body: Record<string, any>): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.post(`${baseUrl}/send-company-profile`, body)
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Actions — Network Listeners
  // ---------------------------------------------------------------------------

  function setupNetworkListeners(): void {
    window.addEventListener('online', () => { networkOnLine.value = true })
    window.addEventListener('offline', () => { networkOnLine.value = false })
  }

  // ---------------------------------------------------------------------------
  // Utilities (private)
  // ---------------------------------------------------------------------------

  function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    appTitle,
    appShortTitle,
    appVersion,
    networkOnLine,
    SWRegistrationForNewContent,
    showSplashScreen,
    splashShown,
    refreshingApp,
    loading,
    menuClosed,
    isPageFullScreen,
    currentLanguage,
    analyticsEnabled,
    deviceID,
    snackbarMessage,
    showSnackbarSuccessMessage,
    showSnackbarInfoMessage,
    showSnackbarErrorMessage,
    userPreferences,
    preferencesDialogOpen,

    // Getters
    isOnline,
    isLoading,
    newContentAvailable,
    getUserPreference,

    // Actions — PWA
    serviceWorkerSkipWaiting,
    dismissServiceWorkerUpdate,
    setSWRegistrationForNewContent,

    // Actions — Init
    initDeviceID,
    initCurrentLanguage,
    changeLanguage,

    // Actions — UI
    toggleMenuClosed,
    setMenuClosed,
    setLoadingState,
    setPageFullScreen,
    setShowSplashScreen,
    setSplashShown,
    setRefreshingApp,
    setNetworkOnline,
    openPreferencesDialog,

    // Actions — Snackbar
    showSnackbar,
    showSuccess,
    showInfo,
    showError,
    resetSnackbar,

    // Actions — User Preferences
    loadUserPreferences,
    setUserPreference,

    // Actions — API
    sendCompanyProfile,

    // Actions — Network
    setupNetworkListeners,
  }
})
